import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Trash2, Copy, Plus } from 'lucide-react';

const API = typeof window !== 'undefined' ? `${window.location.origin}/api` : '/api';
const BASE_URL = typeof window !== 'undefined' ? window.location.origin : '';

const TYPE_LABELS = { casal: 'Casal', solteira: 'Solteira', solteiro: 'Solteiro' };

const MyInvites = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [chosenType, setChosenType] = useState('casal');
  const [createdInvite, setCreatedInvite] = useState(null);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/social-invites/mine`, { headers: authHeaders() });
      setInvites(res.data.invites || []);
    } catch (err) {
      toast.error('Erro ao carregar seus convites');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    if (user.account_kind !== 'free') {
      toast.error('Apenas perfis da Rede Social podem gerar convites');
      navigate('/feed');
      return;
    }
    if (user.social_approval_status === 'pending') {
      // ainda em análise
    }
    load();
  }, [user, navigate, load]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await axios.post(`${API}/social-invites`, { invite_type: chosenType }, { headers: authHeaders() });
      setCreatedInvite(res.data);
      toast.success('Convite criado!');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar convite');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deletar este convite não utilizado?')) return;
    try {
      await axios.delete(`${API}/social-invites/mine/${id}`, { headers: authHeaders() });
      toast.success('Convite deletado');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao deletar');
    }
  };

  const buildUrl = (t) => `${BASE_URL}/?social_invite=${t}`;
  const copy = (text) => navigator.clipboard.writeText(text).then(() => toast.success('Copiado!'));

  const pendingBlocked = user?.social_approval_status === 'pending';

  return (
    <div data-testid="my-invites-page" className="min-h-screen bg-[#0D0D0D] text-white p-4 sm:p-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-white/5">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-[#C79F5E]">Meus Convites</h1>
            <p className="text-gray-400 text-sm mt-1">
              Convide novos casais / solteiras / solteiros para a Rede Social. Cada convidado passa por análise do admin antes de acessar o feed.
            </p>
          </div>
        </div>

        {pendingBlocked && (
          <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/40 rounded-xl text-yellow-200 text-sm">
            ⏳ Sua conta ainda está em análise pelo admin. Assim que for aprovada, você poderá gerar convites.
          </div>
        )}

        <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-2xl p-4 mb-6">
          <p className="text-sm font-semibold text-white mb-3">Gerar novo convite</p>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <select
              value={chosenType}
              onChange={(e) => setChosenType(e.target.value)}
              className="bg-[#0D0D0D] border border-[#2A2A2A] rounded-lg px-3 py-2 text-white text-sm"
              disabled={pendingBlocked}
              data-testid="my-invite-type"
            >
              <option value="casal">Casal</option>
              <option value="solteira">Solteira</option>
              <option value="solteiro">Solteiro</option>
            </select>
            <button
              onClick={handleCreate}
              disabled={creating || pendingBlocked}
              className="flex items-center gap-2 bg-gradient-to-r from-[#C79F5E] to-[#A67C3D] text-black font-bold px-4 py-2 rounded-lg hover:scale-[1.02] disabled:opacity-50"
              data-testid="my-invite-create-btn"
            >
              <Plus className="w-4 h-4" /> {creating ? 'Criando...' : 'Gerar Convite'}
            </button>
          </div>
          {createdInvite && (
            <div className="mt-3 p-3 bg-[#0D0D0D] border border-[#C79F5E]/50 rounded-xl">
              <p className="text-xs text-gray-400">Link do convite ({TYPE_LABELS[createdInvite.invite_type]}):</p>
              <div className="mt-1 flex items-center gap-2">
                <code className="flex-1 text-xs bg-black/40 px-2 py-1.5 rounded break-all">{buildUrl(createdInvite.invite_token)}</code>
                <button onClick={() => copy(buildUrl(createdInvite.invite_token))} className="p-2 bg-[#C79F5E]/20 hover:bg-[#C79F5E]/30 rounded-lg text-[#C79F5E]">
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <p className="text-xs text-yellow-300 mt-2">⚠️ Convidados por perfis Free passam pela análise do admin antes de acessar o feed.</p>
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center text-gray-400 py-8">Carregando...</div>
        ) : invites.length === 0 ? (
          <div className="text-center text-gray-400 py-12 border border-[#2A2A2A] rounded-2xl bg-[#111]">
            Você ainda não gerou convites.
          </div>
        ) : (
          <div className="space-y-2">
            {invites.map((inv) => (
              <div key={inv.id} className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl p-3 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="bg-[#C79F5E]/20 text-[#C79F5E] text-xs font-bold px-2 py-0.5 rounded-full">{TYPE_LABELS[inv.invite_type]}</span>
                    {inv.used ? (
                      <span className="bg-green-500/20 text-green-400 text-xs font-bold px-2 py-0.5 rounded-full">usado</span>
                    ) : (
                      <span className="bg-yellow-500/20 text-yellow-300 text-xs font-bold px-2 py-0.5 rounded-full">disponível</span>
                    )}
                    <code className="text-xs text-gray-300 truncate">{inv.token}</code>
                  </div>
                  {inv.used_by_user && (
                    <div className="text-xs text-gray-500 mt-1">
                      Usado por <strong className="text-white">{inv.used_by_user.name}</strong>
                      {inv.used_by_user.social_approval_status === 'pending' && <span className="ml-2 text-yellow-300">• em análise</span>}
                      {inv.used_by_user.social_approval_status === 'approved' && <span className="ml-2 text-green-400">• aprovado</span>}
                      {inv.used_by_user.social_approval_status === 'rejected' && <span className="ml-2 text-red-400">• rejeitado</span>}
                    </div>
                  )}
                </div>
                <div className="flex gap-2 sm:justify-end">
                  <button onClick={() => copy(buildUrl(inv.token))} className="px-3 py-2 rounded-lg bg-[#C79F5E]/20 hover:bg-[#C79F5E]/30 text-[#C79F5E] text-xs font-semibold flex items-center gap-1">
                    <Copy className="w-3.5 h-3.5" /> Copiar
                  </button>
                  {!inv.used && (
                    <button onClick={() => handleDelete(inv.id)} className="px-3 py-2 rounded-lg bg-red-900/30 hover:bg-red-900/50 text-red-300 text-xs font-semibold flex items-center gap-1">
                      <Trash2 className="w-3.5 h-3.5" /> Excluir
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyInvites;
